import {
  CareTeamEscalation,
  CareTeamEscalationChannel,
  careTeamEscalationChannelDisplayValues,
  CareTeamEscalationReason,
  careTeamEscalationReasonDisplayValues
} from '@/api/dto/dto';
import {CareJourneyEventType, taskTypeDisplayValues} from '@/api/dto/care-journey';
import {nonGroupedValues} from '@common/FilterComponent';

export const channelOptions = Object.values(CareTeamEscalationChannel)
  .map(channel => ({
    label: careTeamEscalationChannelDisplayValues[channel],
    value: channel as string
  }));

export const reasonOptions: nonGroupedValues[] = Object.values(CareTeamEscalationReason)
  .map(reason => ({
    id: reason,
    value: reason,
    label: careTeamEscalationReasonDisplayValues[reason]
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const emptyEscalation = (): CareTeamEscalation => ({
  journeyType: null,
  enabled: false,
  buttonLabel: null,
  confirmationText: null,
  channel: null,
  reasonRequired: false,
  escalationReasons: []
});

export type EscalationField = keyof Pick<CareTeamEscalation,
  'journeyType' | 'buttonLabel' | 'channel' | 'escalationReasons'>;

export type EscalationErrors = Partial<Record<EscalationField, string>>;

/** The task types already spoken for by other entries. */
export const journeyTypesInUse = (
  escalations: CareTeamEscalation[],
  excludeIndex: number | null
): string[] =>
  escalations
    .filter((_, i) => i !== excludeIndex)
    .map(escalation => escalation.journeyType)
    .filter((journeyType): journeyType is CareJourneyEventType => !!journeyType);

// A group can have at most one escalation per task type
export const availableJourneyTypeOptions = (
  escalations: CareTeamEscalation[],
  excludeIndex: number | null
) => {
  const inUse = journeyTypesInUse(escalations, excludeIndex);

  return Object.values(CareJourneyEventType)
    .filter(eventType => eventType !== CareJourneyEventType.FLOWABLE_WORKFLOW)
    .filter(eventType => !inUse.includes(eventType))
    .map(eventType => ({label: taskTypeDisplayValues[eventType], value: eventType as string}));
};

export const escalationErrors = (
  escalation: CareTeamEscalation,
  escalations: CareTeamEscalation[],
  index: number | null
): EscalationErrors => {
  const errors: EscalationErrors = {};

  if (!escalation.journeyType) {
    errors.journeyType = 'Task type is required';
  } else if (journeyTypesInUse(escalations, index).includes(escalation.journeyType)) {
    errors.journeyType = 'This task type is already configured';
  }

  if (!escalation.buttonLabel?.trim()) {
    errors.buttonLabel = 'Button label is required';
  }

  if (!escalation.channel) {
    errors.channel = 'Channel is required';
  }

  // Reasons are only mandatory when the customer wants the navigator to give one.
  if (escalation.reasonRequired && !escalation.escalationReasons?.length) {
    errors.escalationReasons = 'Select at least one reason';
  }

  return errors;
};

export const reasonsSummary = (escalation: CareTeamEscalation) => {
  if (!escalation.reasonRequired) return 'Not required';
  const count = escalation.escalationReasons?.length ?? 0;
  return count === 0 ? 'Required · none selected' : `Required · ${count} selected`;
};

export const selectedReasonOptions = (escalation: CareTeamEscalation): nonGroupedValues[] =>
  (escalation.escalationReasons ?? []).map(code =>
    reasonOptions.find(reason => reason.value === code) ?? {id: code, value: code, label: code});
